import { OrderStatus, OrderType, OutputTemplateDocumentType, PurchaseOrderStatus } from "@prisma/client";
import { z } from "zod";

import { orgSettingKeys } from "~/shared/constants/org.constants";
import { assertBolDocumentOrgSettingValueForWrite } from "~/shared/documents/schemas/bol-document-config.schema";
import {
  type BorderCanadaDocumentConfig,
  BorderCanadaDocumentConfigObjectSchema,
  BorderCanadaDocumentConfigSchema,
} from "~/shared/documents/schemas/border-canada-config.schema";
import {
  type BorderUsDocumentConfig,
  BorderUsDocumentConfigObjectSchema,
  BorderUsDocumentConfigSchema,
} from "~/shared/documents/schemas/border-us-config.schema";
import { DocumentDisplayUomSettingsSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { assertInvoiceDocumentOrgSettingValueForWrite } from "~/shared/documents/schemas/invoice-document-config.schema";
import { assertOcDocumentOrgSettingValueForWrite } from "~/shared/documents/schemas/oc-document-org-setting-write";
import { assertPackingSlipDocumentOrgSettingValueForWrite } from "~/shared/documents/schemas/packing-slip-document-config.schema";
import { assertPickUpRequestDocumentOrgSettingValueForWrite } from "~/shared/documents/schemas/pick-up-request-document-config.schema";
import {
  type PickupInstructionsDocumentConfig,
  PickupInstructionsDocumentConfigSchema,
  parsePickupInstructionsDocumentConfig,
} from "~/shared/documents/schemas/pickup-instructions-config.schema";
import { AllowTypedSignatureSettingsSchema } from "~/shared/documents/schemas/typed-signature.schema";
import { assertWarehouseReleaseDocumentOrgSettingValueForWrite } from "~/shared/documents/schemas/warehouse-release-document-config.schema";
import { DirectShipOrderTypes } from "~/shared/orders/order-type";
import { type EditorSchemaType, emptyEditorDoc, sanitizeEditorDocForPdf } from "~/utils/types/rich-text";

/** Canonical slugs for opt-in order documents. Registry `id` must match these values. */
export const ORDER_DOCUMENT_SLUG = {
  borderUs: "border-us",
  borderCanada: "border-canada",
  pickupInstructions: "pickup-instructions",
} as const;

export const ORDER_DOCUMENT_SLUGS = [
  ORDER_DOCUMENT_SLUG.borderUs,
  ORDER_DOCUMENT_SLUG.borderCanada,
  ORDER_DOCUMENT_SLUG.pickupInstructions,
] as const;

export type OrderDocumentSlug = (typeof ORDER_DOCUMENT_SLUGS)[number];

/**
 * An opt-in slug's document slot in the output-template catalogue (SC-3349).
 *
 * The enum value is the SCREAMING_SNAKE spelling of the slug, which is the rule SC-3794 chose so
 * the two vocabularies could not drift. This makes the mapping executable rather than a
 * convention: `satisfies Record<OrderDocumentSlug, …>` means a fourth slug without a slot fails
 * the build, which is the same thing the coverage test asserts from the other direction.
 */
export const OUTPUT_TEMPLATE_DOCUMENT_TYPE_BY_ORDER_DOCUMENT_SLUG = {
  [ORDER_DOCUMENT_SLUG.borderUs]: OutputTemplateDocumentType.BORDER_US,
  [ORDER_DOCUMENT_SLUG.borderCanada]: OutputTemplateDocumentType.BORDER_CANADA,
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: OutputTemplateDocumentType.PICKUP_INSTRUCTIONS,
} satisfies Record<OrderDocumentSlug, OutputTemplateDocumentType>;

export const OrderDocumentSlugSchema = z.enum(ORDER_DOCUMENT_SLUGS);

/**
 * Default lifecycle for opt-in SO documents: draft through delivered (no warehouse gate like BOL).
 * Registry entries reference this set unless a slug needs a narrower window.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 * @see docs/ORDER_LIFECYCLE_AND_DATA_CONSISTENCY.md
 */
export const OPT_IN_ORDER_DOCUMENT_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set([
  OrderStatus.DRAFT,
  OrderStatus.CONFIRMED,
  OrderStatus.SHIPPED,
  OrderStatus.DELIVERED,
]);

/** Primary action on opt-in order document modals — generates and opens the PDF. */
export const OPT_IN_ORDER_DOCUMENT_SUBMIT_LABEL = "Generate PDF";

/** Stored at `orgSettingKeys.orderDocumentsEnabled` as JSON string array. */
export const OrderDocumentsEnabledSchema = z.array(OrderDocumentSlugSchema);

export type { BorderCanadaDocumentConfig, BorderUsDocumentConfig, PickupInstructionsDocumentConfig };
export { BorderCanadaDocumentConfigSchema, BorderUsDocumentConfigSchema, PickupInstructionsDocumentConfigSchema };

/** Optional per-slug rollout constraints (org base currency, etc.). */
export type OrderDocumentSlugRequirements = {
  requiredOrgBaseCurrencyCode?: string;
};

/** Per-slug product metadata — single source for menu labels, enablement errors, PDF file prefixes, settings nav. */
export type OrderDocumentSlugMetadata = {
  menuLabel: string;
  enablementErrorLabel: string;
  filePrefix: string;
  /** PDF header title when different from menu label (defaults to menuLabel). */
  pdfTitle?: string;
  /** `searchWords`: the words the Settings menu search finds this page by (rules in settings-search-words.ts). */
  settingsNav: { label: string; href: string; searchWords: readonly string[] };
};

export const orderDocumentMetadataBySlug = {
  [ORDER_DOCUMENT_SLUG.borderUs]: {
    menuLabel: "Border U.S.",
    enablementErrorLabel: "Border U.S. documents",
    filePrefix: "Border U.S.",
    settingsNav: {
      label: "Border U.S.",
      href: "/settings/order-documents/border-us",
      searchWords: [
        "customs",
        "customs clearance",
        "declaration of shipper",
        "HS code",
        "HTS",
        "tariff",
        "duty",
        "country of manufacture",
        "typed signature",
        "pro forma",
      ],
    },
  },
  [ORDER_DOCUMENT_SLUG.borderCanada]: {
    menuLabel: "Border Canadian",
    enablementErrorLabel: "Border Canadian documents",
    filePrefix: "Border Canadian",
    settingsNav: {
      label: "Border Canadian",
      href: "/settings/order-documents/border-canada",
      searchWords: ["customs", "customs clearance form", "Canada", "tariff number", "duty", "typed signature"],
    },
  },
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: {
    menuLabel: "Carrier Instructions",
    enablementErrorLabel: "Carrier Instructions documents",
    filePrefix: "Carrier Instructions",
    pdfTitle: "Carrier Instructions",
    settingsNav: {
      label: "Carrier Instructions",
      href: "/settings/order-documents/pickup-instructions",
      searchWords: [
        "pickup instructions",
        "delivery instructions",
        "seal numbers",
        "wash ticket",
        "demurrage",
        "typed signature",
        "drop ship",
      ],
    },
  },
} as const satisfies Record<OrderDocumentSlug, OrderDocumentSlugMetadata>;

export const orderDocumentEnablementErrorLabel = (slug: OrderDocumentSlug): string =>
  orderDocumentMetadataBySlug[slug].enablementErrorLabel;

export const orderDocumentFilePrefix = (slug: OrderDocumentSlug): string => orderDocumentMetadataBySlug[slug].filePrefix;

export const orderDocumentPdfTitle = (slug: OrderDocumentSlug): string => {
  const metadata: OrderDocumentSlugMetadata = orderDocumentMetadataBySlug[slug];
  return metadata.pdfTitle ?? metadata.menuLabel;
};

/** Per-slug requirements — extend when new opt-in documents add constraints (e.g. lifecycle-only). */
export const orderDocumentRequirementsBySlug: Partial<Record<OrderDocumentSlug, OrderDocumentSlugRequirements>> = {};

/** Per-slug SO eligibility beyond lifecycle (order type, linked PO, etc.). */
export type OrderDocumentSlugOrderRequirements = {
  orderTypes?: readonly OrderType[];
  linkedPurchaseOrderRequiredFor?: readonly OrderType[];
};

export type OrderDocumentOrderEligibilityContext = {
  type: OrderType;
  purchaseOrderId: string | null;
};

/** Per-slug order eligibility — see slug sections in docs/OPT_IN_ORDER_DOCUMENTS.md */
export const orderDocumentOrderRequirementsBySlug: Partial<Record<OrderDocumentSlug, OrderDocumentSlugOrderRequirements>> = {
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: {
    orderTypes: [OrderType.STOCK_ORDER, ...DirectShipOrderTypes],
    linkedPurchaseOrderRequiredFor: DirectShipOrderTypes,
  },
};

export const isOrderEligibleForOrderDocumentSlug = (slug: OrderDocumentSlug, order: OrderDocumentOrderEligibilityContext): boolean => {
  const requirements = orderDocumentOrderRequirementsBySlug[slug];
  if (!requirements) {
    return true;
  }
  if (requirements.orderTypes && !requirements.orderTypes.includes(order.type)) {
    return false;
  }
  if (requirements.linkedPurchaseOrderRequiredFor?.includes(order.type) && order.purchaseOrderId == null) {
    return false;
  }
  return true;
};

export const orderDocumentOrderIneligibilityMessage = (slug: OrderDocumentSlug): string | undefined => {
  const requirements = orderDocumentOrderRequirementsBySlug[slug];
  if (!requirements) {
    return undefined;
  }
  if (slug === ORDER_DOCUMENT_SLUG.pickupInstructions) {
    return `${orderDocumentMetadataBySlug[slug].menuLabel} are only available on stock orders, or direct ship or blind ship orders with a linked purchase order`;
  }
  return `${orderDocumentEnablementErrorLabel(slug)} are not available for this order type`;
};

export const requiredOrgBaseCurrencyForOrderDocumentSlug = (slug: OrderDocumentSlug): string | undefined =>
  orderDocumentRequirementsBySlug[slug]?.requiredOrgBaseCurrencyCode;

export const ORDER_DOCUMENT_CONFIG_KEY_PREFIX = "ORDER_DOCUMENT_CONFIG:";

/** Per-slug config row key, e.g. `ORDER_DOCUMENT_CONFIG:border-us`. */
export const orderDocumentConfigKey = (slug: OrderDocumentSlug): string => `${ORDER_DOCUMENT_CONFIG_KEY_PREFIX}${slug}`;

const configObjectSchemaBySlug = {
  [ORDER_DOCUMENT_SLUG.borderUs]: BorderUsDocumentConfigObjectSchema,
  [ORDER_DOCUMENT_SLUG.borderCanada]: BorderCanadaDocumentConfigObjectSchema,
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: PickupInstructionsDocumentConfigSchema,
} as const satisfies Record<OrderDocumentSlug, z.ZodType>;

const configSchemaBySlug = {
  [ORDER_DOCUMENT_SLUG.borderUs]: BorderUsDocumentConfigSchema,
  [ORDER_DOCUMENT_SLUG.borderCanada]: BorderCanadaDocumentConfigSchema,
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: PickupInstructionsDocumentConfigSchema,
} as const satisfies Record<OrderDocumentSlug, z.ZodType>;

export const emptyBorderUsDocumentConfig = (): BorderUsDocumentConfig =>
  BorderUsDocumentConfigSchema.parse({
    header: emptyEditorDoc(),
    customsNotify: emptyEditorDoc(),
    declarationOfShipper: emptyEditorDoc(),
  });

export const emptyBorderCanadaDocumentConfig = (): BorderCanadaDocumentConfig =>
  BorderCanadaDocumentConfigSchema.parse({
    header: emptyEditorDoc(),
  });

export const emptyPickupInstructionsDocumentConfig = (): PickupInstructionsDocumentConfig =>
  PickupInstructionsDocumentConfigSchema.parse({
    deliveryInstructionsHeader: emptyEditorDoc(),
    sealWashTicketBoilerplate: emptyEditorDoc(),
    demurrageBoilerplate: emptyEditorDoc(),
  });

const emptyConfigBySlug = {
  [ORDER_DOCUMENT_SLUG.borderUs]: emptyBorderUsDocumentConfig,
  [ORDER_DOCUMENT_SLUG.borderCanada]: emptyBorderCanadaDocumentConfig,
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: emptyPickupInstructionsDocumentConfig,
} as const satisfies Record<
  OrderDocumentSlug,
  () => BorderUsDocumentConfig | BorderCanadaDocumentConfig | PickupInstructionsDocumentConfig
>;

/** True when order status is in the registry entry's allowed lifecycle set. */
export const canGenerateOptInOrderDocumentForStatus = (status: OrderStatus, allowedStatuses: ReadonlySet<OrderStatus>): boolean =>
  allowedStatuses.has(status);

export type OrderDocumentsEnabledParseResult = {
  slugs: OrderDocumentSlug[];
  /** True when raw was non-empty but JSON or slug validation failed (fail-soft read path). */
  hadCorruptRaw: boolean;
};

export const parseOrderDocumentsEnabledDiagnostics = (raw: string | null | undefined): OrderDocumentsEnabledParseResult => {
  if (!raw?.trim()) {
    return { slugs: [], hadCorruptRaw: false };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = OrderDocumentsEnabledSchema.safeParse(parsed);
    if (result.success) {
      return { slugs: result.data, hadCorruptRaw: false };
    }
    return { slugs: [], hadCorruptRaw: true };
  } catch {
    return { slugs: [], hadCorruptRaw: true };
  }
};

export const parseOrderDocumentsEnabled = (raw: string | null | undefined): OrderDocumentSlug[] =>
  parseOrderDocumentsEnabledDiagnostics(raw).slugs;

/** Strict parse for super-admin writes; throws on invalid JSON or unknown slugs. */
export const parseOrderDocumentsEnabledForWrite = (raw: string): OrderDocumentSlug[] => {
  if (!raw.trim()) {
    throw new Error("ORDER_DOCUMENTS_ENABLED value must be a JSON array of document slugs");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("ORDER_DOCUMENTS_ENABLED must be valid JSON");
  }
  const result = OrderDocumentsEnabledSchema.safeParse(parsed);
  if (!result.success) {
    throw new Error(`ORDER_DOCUMENTS_ENABLED must be a JSON array of known document slugs (${ORDER_DOCUMENT_SLUGS.join(", ")})`);
  }
  return result.data;
};

/** Strict parse for super-admin config writes; throws on invalid JSON or schema mismatch. */
export const parseOrderDocumentConfigForWrite = <TSlug extends OrderDocumentSlug>(slug: TSlug, raw: string): void => {
  if (!raw.trim()) {
    throw new Error(`ORDER_DOCUMENT_CONFIG:${slug} value must be a JSON config object`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(`ORDER_DOCUMENT_CONFIG:${slug} must be valid JSON`);
  }
  const result = configSchemaBySlug[slug].safeParse(parsed);
  if (!result.success) {
    throw new Error(`ORDER_DOCUMENT_CONFIG:${slug} does not match the document config schema`);
  }
};

export type AssertOrderDocumentOrgSettingValueOptions = {
  /** Required when enabling slugs that declare `requiredOrgBaseCurrencyCode`. */
  orgBaseCurrencyCode?: string;
};

/**
 * Validates order-document and always-on document org setting values on super-admin write paths.
 * No-op for unrelated keys.
 */
export const assertOrderDocumentOrgSettingValueForWrite = (
  key: string,
  value: string,
  options?: AssertOrderDocumentOrgSettingValueOptions,
): void => {
  if (key === orgSettingKeys.ocDocumentConfig) {
    assertOcDocumentOrgSettingValueForWrite(value);
    return;
  }
  if (key === orgSettingKeys.bolDocumentConfig) {
    assertBolDocumentOrgSettingValueForWrite(value);
    return;
  }
  if (key === orgSettingKeys.packingSlipDocumentConfig) {
    assertPackingSlipDocumentOrgSettingValueForWrite(value);
    return;
  }
  if (key === orgSettingKeys.warehouseReleaseDocumentConfig) {
    assertWarehouseReleaseDocumentOrgSettingValueForWrite(value);
    return;
  }
  if (key === orgSettingKeys.invoiceDocumentConfig) {
    assertInvoiceDocumentOrgSettingValueForWrite(value);
    return;
  }
  if (key === orgSettingKeys.pickUpRequestDocumentConfig) {
    assertPickUpRequestDocumentOrgSettingValueForWrite(value);
    return;
  }
  if (key === orgSettingKeys.orderDocumentsEnabled) {
    const slugs = parseOrderDocumentsEnabledForWrite(value);
    if (options?.orgBaseCurrencyCode) {
      for (const slug of slugs) {
        const requiredBase = requiredOrgBaseCurrencyForOrderDocumentSlug(slug);
        if (requiredBase && options.orgBaseCurrencyCode !== requiredBase) {
          throw new Error(
            `Cannot enable ${orderDocumentEnablementErrorLabel(slug)} when org base currency is ${
              options.orgBaseCurrencyCode
            }; requires ${requiredBase}`,
          );
        }
      }
    }
    return;
  }
  if (!key.startsWith(ORDER_DOCUMENT_CONFIG_KEY_PREFIX)) {
    return;
  }
  const slugPart = key.slice(ORDER_DOCUMENT_CONFIG_KEY_PREFIX.length);
  const slugResult = OrderDocumentSlugSchema.safeParse(slugPart);
  if (!slugResult.success) {
    throw new Error(`Unknown order document slug in config key "${key}"; expected one of: ${ORDER_DOCUMENT_SLUGS.join(", ")}`);
  }
  parseOrderDocumentConfigForWrite(slugResult.data, value);
};

/** Super-admin delete must not remove order-document rows — use `[]` or overwrite config instead. */
export const assertOrderDocumentOrgSettingKeyNotDeletable = (key: string): void => {
  if (
    key === orgSettingKeys.orderDocumentsEnabled ||
    key === orgSettingKeys.packingSlipDocumentConfig ||
    key === orgSettingKeys.warehouseReleaseDocumentConfig ||
    key.startsWith(ORDER_DOCUMENT_CONFIG_KEY_PREFIX)
  ) {
    throw new Error(
      `Cannot delete order document org setting "${key}"; set ORDER_DOCUMENTS_ENABLED to [] or overwrite the config value instead`,
    );
  }
};

export const serializeOrderDocumentsEnabled = (slugs: readonly OrderDocumentSlug[]): string => JSON.stringify([...new Set(slugs)]);

/** Settings → Document options toggles (maps to `ORDER_DOCUMENTS_ENABLED` slug array). */
export const OrderDocumentsEnabledTogglesSchema = z.object({
  borderUs: z.boolean(),
  borderCanada: z.boolean(),
  pickupInstructions: z.boolean(),
});

export type OrderDocumentsEnabledToggles = z.infer<typeof OrderDocumentsEnabledTogglesSchema>;

/** Maps opt-in slug → Document options toggle key (single source for UI + serialization). */
export const orderDocumentSlugToToggleKey: Record<OrderDocumentSlug, keyof OrderDocumentsEnabledToggles> = {
  [ORDER_DOCUMENT_SLUG.borderUs]: "borderUs",
  [ORDER_DOCUMENT_SLUG.borderCanada]: "borderCanada",
  [ORDER_DOCUMENT_SLUG.pickupInstructions]: "pickupInstructions",
};

export const orderDocumentsEnabledTogglesFromSlugs = (slugs: readonly OrderDocumentSlug[]): OrderDocumentsEnabledToggles => ({
  borderUs: slugs.includes(ORDER_DOCUMENT_SLUG.borderUs),
  borderCanada: slugs.includes(ORDER_DOCUMENT_SLUG.borderCanada),
  pickupInstructions: slugs.includes(ORDER_DOCUMENT_SLUG.pickupInstructions),
});

export const orderDocumentSlugsFromEnabledToggles = (toggles: OrderDocumentsEnabledToggles): OrderDocumentSlug[] => {
  const slugs: OrderDocumentSlug[] = [];
  if (toggles.borderUs) {
    slugs.push(ORDER_DOCUMENT_SLUG.borderUs);
  }
  if (toggles.borderCanada) {
    slugs.push(ORDER_DOCUMENT_SLUG.borderCanada);
  }
  if (toggles.pickupInstructions) {
    slugs.push(ORDER_DOCUMENT_SLUG.pickupInstructions);
  }
  return slugs;
};

/** True when a per-slug config org-setting row exists (config is preserved when enablement is off). */
export const hasPersistedOrderDocumentConfigRaw = (raw: string | null | undefined): boolean => raw != null && raw.trim() !== "";

export const isOrderDocumentSlugEnabled = (enabledSlugs: ReadonlySet<OrderDocumentSlug>, slug: OrderDocumentSlug): boolean =>
  enabledSlugs.has(slug);

const parsedOrderDocumentConfigFromRaw = <TSlug extends OrderDocumentSlug>(
  slug: TSlug,
  parsed: unknown,
): z.infer<(typeof configSchemaBySlug)[TSlug]> | null => {
  if (slug === ORDER_DOCUMENT_SLUG.pickupInstructions) {
    // Slug branch guarantees PickupInstructionsDocumentConfig | null from parsePickupInstructionsDocumentConfig.
    return parsePickupInstructionsDocumentConfig(parsed) as z.infer<(typeof configSchemaBySlug)[TSlug]> | null;
  }
  const result = configSchemaBySlug[slug].safeParse(parsed);
  return result.success ? (result.data as z.infer<(typeof configSchemaBySlug)[TSlug]>) : null;
};

/** True when raw was non-empty but JSON or config schema validation failed (fail-soft read path). */
export const hadCorruptOrderDocumentConfigRaw = (slug: OrderDocumentSlug, raw: string | null | undefined): boolean => {
  if (!raw?.trim()) {
    return false;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsedOrderDocumentConfigFromRaw(slug, parsed) === null;
  } catch {
    return true;
  }
};

export const parseOrderDocumentConfig = <TSlug extends OrderDocumentSlug>(
  slug: TSlug,
  raw: string | null | undefined,
): z.infer<(typeof configSchemaBySlug)[TSlug]> => {
  if (!raw?.trim()) {
    return emptyConfigBySlug[slug]() as z.infer<(typeof configSchemaBySlug)[TSlug]>;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const config = parsedOrderDocumentConfigFromRaw(slug, parsed);
    if (config) {
      return config;
    }
  } catch {
    // fall through to empty defaults
  }
  return emptyConfigBySlug[slug]() as z.infer<(typeof configSchemaBySlug)[TSlug]>;
};

const sanitizeOrderDocumentConfigEditors = <TSlug extends OrderDocumentSlug>(
  slug: TSlug,
  config: z.infer<(typeof configSchemaBySlug)[TSlug]>,
): z.infer<(typeof configSchemaBySlug)[TSlug]> => {
  if (slug === ORDER_DOCUMENT_SLUG.borderUs) {
    const borderUs = config as BorderUsDocumentConfig;
    return {
      displayUom: borderUs.displayUom,
      allowDisplayUomOverrideOnCreate: borderUs.allowDisplayUomOverrideOnCreate,
      allowTypedSignature: borderUs.allowTypedSignature,
      attributeWiring: borderUs.attributeWiring,
      header: sanitizeEditorDocForPdf(borderUs.header),
      customsNotify: sanitizeEditorDocForPdf(borderUs.customsNotify),
      declarationOfShipper: sanitizeEditorDocForPdf(borderUs.declarationOfShipper),
    } as z.infer<(typeof configSchemaBySlug)[TSlug]>;
  }
  if (slug === ORDER_DOCUMENT_SLUG.borderCanada) {
    const borderCanada = config as BorderCanadaDocumentConfig;
    return {
      displayUom: borderCanada.displayUom,
      allowDisplayUomOverrideOnCreate: borderCanada.allowDisplayUomOverrideOnCreate,
      allowTypedSignature: borderCanada.allowTypedSignature,
      attributeWiring: borderCanada.attributeWiring,
      header: sanitizeEditorDocForPdf(borderCanada.header),
    } as z.infer<(typeof configSchemaBySlug)[TSlug]>;
  }
  const pickupInstructions = config as PickupInstructionsDocumentConfig;
  return {
    displayUom: pickupInstructions.displayUom,
    allowDisplayUomOverrideOnCreate: pickupInstructions.allowDisplayUomOverrideOnCreate,
    allowTypedSignature: pickupInstructions.allowTypedSignature,
    deliveryInstructionsHeader: sanitizeEditorDocForPdf(pickupInstructions.deliveryInstructionsHeader),
    sealWashTicketBoilerplate: sanitizeEditorDocForPdf(pickupInstructions.sealWashTicketBoilerplate),
    demurrageBoilerplate: sanitizeEditorDocForPdf(pickupInstructions.demurrageBoilerplate),
  } as z.infer<(typeof configSchemaBySlug)[TSlug]>;
};

export const serializeOrderDocumentConfig = <TSlug extends OrderDocumentSlug>(
  slug: TSlug,
  config: z.input<(typeof configObjectSchemaBySlug)[TSlug]>,
): string => {
  const validated = configSchemaBySlug[slug].parse(config);
  const sanitized = sanitizeOrderDocumentConfigEditors(slug, validated);
  return JSON.stringify(sanitized);
};

export const GetOrderDocumentSettingsInputSchema = z.object({
  slug: OrderDocumentSlugSchema,
});

/**
 * Create-modal org flags for opt-in slugs (display UOM + typed signature).
 * Pickup-instructions typed signature follows the same org allow-toggle as border slugs.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
export const OrderDocumentCreateModalSettingsSchema = DocumentDisplayUomSettingsSchema.merge(AllowTypedSignatureSettingsSchema);

export type OrderDocumentCreateModalSettings = z.infer<typeof OrderDocumentCreateModalSettingsSchema>;

export const orderDocumentCreateModalSettingsFromConfig = (
  config: BorderUsDocumentConfig | BorderCanadaDocumentConfig | PickupInstructionsDocumentConfig,
): OrderDocumentCreateModalSettings => ({
  displayUom: config.displayUom,
  allowDisplayUomOverrideOnCreate: config.allowDisplayUomOverrideOnCreate,
  allowTypedSignature: "allowTypedSignature" in config ? config.allowTypedSignature : false,
});

export const UpdateOrderDocumentSettingsInputSchema = z.discriminatedUnion("slug", [
  BorderUsDocumentConfigObjectSchema.extend({ slug: z.literal(ORDER_DOCUMENT_SLUG.borderUs) }),
  BorderCanadaDocumentConfigObjectSchema.extend({ slug: z.literal(ORDER_DOCUMENT_SLUG.borderCanada) }),
  PickupInstructionsDocumentConfigSchema.extend({ slug: z.literal(ORDER_DOCUMENT_SLUG.pickupInstructions) }),
]);

/** Editor block HTML for PDF templates; empty editor → null. HTML is sanitized for PDF safety. */
export const editorSettingHtml = (doc: EditorSchemaType): string | null => (doc.text.trim() ? sanitizeEditorDocForPdf(doc).html : null);

/**
 * Opt-in order document org settings:
 * - Enablement: `orgSettingKeys.orderDocumentsEnabled` → JSON slug array
 * - Config: `orderDocumentConfigKey(slug)` → JSON per slug (see schemas above)
 *
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 * @see order-menu-document-extensions/registry.ts
 */

/** Completed standalone POs retain document issuance without reopening operational edits. */
export const canGenerateOptInPurchaseOrderDocumentForStatus = (status: PurchaseOrderStatus): boolean =>
  status === PurchaseOrderStatus.DRAFT || status === PurchaseOrderStatus.CONFIRMED || status === PurchaseOrderStatus.COMPLETED;
